import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { User, Users, Building2, CreditCard, Plug2 } from 'lucide-react'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { usePermissions, PERMISSIONS } from '@/lib/auth/usePermissions'

interface SettingsTab {
    value: string
    label: string
    icon: React.ElementType
    permission: string | null  // null = always visible
}

const TABS: SettingsTab[] = [
    { value: 'profile',       label: 'Profile',       icon: User,      permission: null },
    { value: 'team',          label: 'Team',          icon: Users,     permission: PERMISSIONS.TEAM_VIEW },
    { value: 'organization',  label: 'Organization',  icon: Building2, permission: PERMISSIONS.ORGANIZATION_VIEW },
    { value: 'subscriptions', label: 'Subscriptions', icon: CreditCard,permission: PERMISSIONS.BILLING_VIEW },
    { value: 'integrations',  label: 'Integrations',  icon: Plug2,     permission: PERMISSIONS.INTEGRATIONS_VIEW },
]

interface SettingsLayoutProps {
    children: React.ReactNode
}

export default function SettingsLayout({ children }: SettingsLayoutProps) {
    const navigate = useNavigate()
    const location = useLocation()
    const { hasPermission } = usePermissions()

    const getActiveTab = () => {
        if (location.pathname.includes('/settings/team'))          return 'team'
        if (location.pathname.includes('/settings/organization'))  return 'organization'
        if (location.pathname.includes('/settings/subscriptions')) return 'subscriptions'
        if (location.pathname.includes('/settings/integrations'))  return 'integrations'
        return 'profile'
    }

    const visibleTabs = TABS.filter(
        (tab) => tab.permission === null || hasPermission(tab.permission)
    )

    // Redirect to profile if user navigates directly to a tab they can't access
    useEffect(() => {
        const active = getActiveTab()
        if (active === 'profile') return
        const tab = TABS.find((t) => t.value === active)
        if (tab && tab.permission && !hasPermission(tab.permission)) {
            navigate('/settings/profile', { replace: true })
        }
    }, [location.pathname])

    const handleTabChange = (value: string) => {
        navigate(`/settings/${value}`)
    }

    return (
        <div className="p-8 space-y-6">
            <div>
                <h1 className="text-3xl font-bold">Settings</h1>
                <p className="text-muted-foreground mt-1">
                    Manage your account, team, and organization settings
                </p>
            </div>

            <Tabs value={getActiveTab()} onValueChange={handleTabChange} className="w-full">
                <TabsList style={{ gridTemplateColumns: `repeat(${visibleTabs.length}, minmax(0, 1fr))` }}
                          className="grid w-full max-w-4xl">
                    {visibleTabs.map(({ value, label, icon: Icon }) => (
                        <TabsTrigger key={value} value={value}>
                            <Icon className="h-4 w-4 mr-2" />
                            {label}
                        </TabsTrigger>
                    ))}
                </TabsList>
            </Tabs>

            {children}
        </div>
    )
}